import { colors, containers } from '@ecosystem/newron-design-system';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import BrandDependencyChart from '@data-visualization/brand-dependency-chart/BrandDependencyChart';
import BrandDeepDiveBrandSelector from '../brand-deep-dive-brand-selector/BrandDeepDiveBrandSelector';
import BrandDependencyFilter from '@pages/trading-assistant/brand-dependency-filter/BrandDependencyFilter';
import { CreateViewStateFn, ExportFormat } from '@ecosystem/newron-client-lib';
import {
  BrandDeepDiveChartRespDataLookup,
  BrandDependencyChartSubResp,
  BrandDependencyKpi,
  BrandDependencyKpiDisplayName,
  BrandDependencySort,
} from '@models/BrandDependency';
import { FactRevenue, FeatureOption, Filters, Option, PageName, PageType, SubSection } from '@utils/pageConfig/Types';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { CapturedImage } from '@ecosystem/newron-design-system/build/export-utils/captureImage';
import { useExportMultiChart } from '@hooks/export/utilities/UseExportMultiChart';
import { kpiToOption, sortByToOption } from '@data-visualization/brand-dependency-chart/BrandDependencyChartUtils';
import { TRADING_ASSISTANT_CARD_TITLE } from '@pages/Utilities';
import getComparisonPeriod from '@components/filter-selection-note/GetComparisonPeriod';
import { getAppliedFiltersText, getKpiDisplayNameFromHeaderKey } from '@hooks/export/ExportUtils';
import { BaseTimePeriod, MainFilters } from '@services/FiltersTypes';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  dataExportedTrackingFunction,
  DataExportTrackingElementParam,
  useDataExportTracking,
} from '@utils/page-analytics/model/DataExportTracking';
import { getExportTrackingFormat } from '@hooks/export/export-tracking/ExportTracking';
import { getPeriodicityForMATAndYTD } from '@utils/PeriodUtils';
import { useChartExport } from '@ecosystem/export-utils';
import { useExportTable } from '@hooks/export/export-table/UseExportTable';
import { getVolumeBasedOnPeriodType } from '@utils/factVolumeUtils/FactVolumeUtils';
import { SortDirection, SortingOptions } from '@components/design/migrated/Table/Sorting/types';
import { ProductType } from '@pages/product-analyzer/Product';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { TradingAssistantButterflyChartInsight } from '@components/collections/trading-assistant-butterfly-chart/Model';

export interface BrandDependencyCardProps {
  isLoading: boolean;
  setIsloading: Dispatch<SetStateAction<boolean>>;
  chartData: BrandDependencyChartSubResp[];
  kpi: BrandDependencyKpi;
  setKpi: Dispatch<SetStateAction<BrandDependencyKpi>>;
  sortBy: BrandDependencySort;
  setSortBy: Dispatch<SetStateAction<BrandDependencySort>>;
  bySegment: FeatureOption;
  retailerClientName?: string;
  selectedGrowthType: GrowthTypes;
  factRevenue: FactRevenue;
  features: Filters[];
  isDeepDiveView: boolean;
  viewingBrand: string;
  setViewingBrand: (brand: string, source?: 'restore' | 'kpi_selector' | 'brand_selector') => void;
  deepDiveBrands?: string[];
  deepDiveDataLookup?: Map<string, BrandDeepDiveChartRespDataLookup>;
  setFeatureOptions: Dispatch<SetStateAction<FeatureOption>>;
  subSectionForAmplitudeTracking: SubSection;
  appliedProductGroups: Option[] | undefined;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  timeFrame: Periodicity;
  period: BaseTimePeriod;
  featureOption: FeatureOption;
  appliedCountries: Option[];
  effectiveProductType: ProductType;
  createInsightDataFn?: CreateInsightDataFn<TradingAssistantButterflyChartInsight>;
  captureOnly?: boolean;
  onCaptureComplete?: (images: CapturedImage[]) => void;
  filters?: MainFilters;
  selectedBrands?: Option[];
  createViewStateFn: CreateViewStateFn<any>;
  isApiError?: boolean;
}

// eslint-disable-next-line react/display-name
const BrandDependencyCard: React.FC<BrandDependencyCardProps> = ({
  isLoading,
  setIsloading,
  chartData,
  kpi,
  setKpi,
  sortBy,
  setSortBy,
  bySegment,
  retailerClientName,
  selectedGrowthType,
  factRevenue,
  features,
  isDeepDiveView,
  viewingBrand,
  setViewingBrand,
  deepDiveBrands = [],
  deepDiveDataLookup,
  setFeatureOptions,
  subSectionForAmplitudeTracking,
  appliedCountries,
  period,
  timeFrame,
  comparisonPeriod,
  appliedProductGroups,
  featureOption,
  effectiveProductType,
  createInsightDataFn,
  captureOnly,
  onCaptureComplete,
  filters,
  selectedBrands,
  createViewStateFn,
  isApiError = false,
}) => {
  const pagesConfig = usePageConfig();
  const { Card } = containers;
  const { getBrandColorMap } = colors.useChartColors();
  const brandsColorMap = useRef<Record<string, string>>({});
  const visibleData = isDeepDiveView ? (deepDiveDataLookup?.get(viewingBrand)?.data ?? []) : chartData;

  [...chartData.map((c) => String(c.brand).toUpperCase()), ...(deepDiveBrands || [])]
    .filter((b) => !brandsColorMap.current[b])
    .forEach((b) => {
      brandsColorMap.current[b] = '';
    });
  Object.assign(brandsColorMap.current, getBrandColorMap(Object.keys(brandsColorMap.current)));

  const [exportingAll, setExportingAll] = useState(false);
  const [capturedMap, setCapturedMap] = useState<Record<string, CapturedImage>>({});
  const [exportId, setExportId] = useState(0);
  const [lastFormats, setLastFormats] = useState<ExportFormat[]>([]);
  const singleExportGuard = useRef(false);
  const { download: exportButterflyChart } = useExportMultiChart();
  const handleDataExportTrackingFunction = useDataExportTracking('mainFilters');
  const handleExportedTracking = (
    elementType: ElementType,
    exportFormats: ExportFormat[],
    elementKpi?: string,
    sortingBy?: string,
  ) => {
    const brandDependencySortOption = sortByToOption(BrandDependencySort[sortingBy]);
    const elementSort: SortingOptions = {
      headerKey: brandDependencySortOption.label,
      sortKey: brandDependencySortOption.value,
      sortDir: SortDirection.desc,
      label: brandDependencySortOption.label,
    };

    const param: DataExportTrackingElementParam = {
      elementType,
      elementKpi,
      elementSort,
      optionChosen: exportFormats.map((format) => getExportTrackingFormat(format)),
      brandApplied: selectedBrands?.map((option) => option.value).join(', ') ?? 'All',
    };
    handleDataExportTrackingFunction(param, dataExportedTrackingFunction);
  };

  const [invokeExport] = useChartExport();
  const { download: exportTable } = useExportTable([]);

  const prepareChartExportOptions = (
    exportFormats: ExportFormat[],
    exportImage: CapturedImage[],
    selectedLabel: string,
  ) => ({
    exportFormats,
    title: TRADING_ASSISTANT_CARD_TITLE,
    timePeriod: period.value,
    comparisonTimePeriod: getComparisonPeriod(period, timeFrame, comparisonPeriod),
    factRevenue,
    appliedCountry: appliedCountries,
    appliedPg: appliedProductGroups,
    data: visibleData,
    exportImage,
    periodType: timeFrame,
    appliedFiltersText: getAppliedFiltersText(pagesConfig, PageType.TRADING_ASSISTANT),
    showPerformanceWithin: selectedLabel,
    selectedKpi: featureOption,
    sortedBy: sortBy,
    pageName: PageName.TRADING_ASSISTANT,
    selectedRetailerOption: featureOption,
    chartTitles:
      isDeepDiveView && deepDiveBrands
        ? deepDiveBrands.map(() => PageName.TRADING_ASSISTANT)
        : [PageName.TRADING_ASSISTANT],
    chartSubTitles: isDeepDiveView && deepDiveBrands ? [...deepDiveBrands] : [selectedLabel],
  });

  const handlePptxOrPngExport = async (exportFormats: ExportFormat[], exportImage: CapturedImage[]) => {
    const allKpis = Object.values(BrandDependencyKpi).map(kpiToOption);
    const selected = allKpis.find((opt) => opt.value === kpi) ?? allKpis[0];
    const options = prepareChartExportOptions(exportFormats, exportImage, selected.label);
    if (captureOnly) {
      onCaptureComplete?.(exportImage);
      return;
    }

    try {
      await exportButterflyChart(options);
    } catch (error) {
      console.warn('ppt not exported successfully', error);
    }
  };

  const handleXlsxExport = async (exportFormats: ExportFormat[]) => {
    const periodType = getPeriodicityForMATAndYTD(period, timeFrame).periodicity;
    const showPerformanceWithin = `top 10 Brands by ${BrandDependencyKpiDisplayName[kpi]}`;
    const selectedKpiOption: Option = {
      label: getKpiDisplayNameFromHeaderKey(pagesConfig.sortingOptions.headerKey),
      value: pagesConfig.sortingOptions.headerKey,
    };
    const finalDataLookup = isDeepDiveView ? (deepDiveDataLookup ?? new Map()) : (chartData ?? []);

    try {
      await exportTable({
        exportFormats,
        invokeExport,
        title: TRADING_ASSISTANT_CARD_TITLE,
        appliedCountry: appliedCountries,
        appliedPg: appliedProductGroups,
        data: finalDataLookup,
        summaryData: [],
        timePeriod: period.label,
        comparisonTimePeriod: getComparisonPeriod(period, periodType, comparisonPeriod),
        factRevenue,
        periodType: timeFrame,
        showPerformanceWithin,
        appliedFiltersText: getAppliedFiltersText(pagesConfig, PageType.TRADING_ASSISTANT),
        selectedKpi: selectedKpiOption,
        pageName: PageName.TRADING_ASSISTANT,
        pagesConfig,
        volume: getVolumeBasedOnPeriodType(filters, timeFrame),
        retailClientName: retailerClientName,
        isDeepDiveView,
        segmentBy: featureOption.feature,
        sortedDeepDiveBrands: deepDiveBrands,
      });
    } catch (err) {
      throw new Error(`Error while exporting - ${err}`);
    }
  };

  const onChartDownload = useCallback(
    async (exportFormats: ExportFormat[], exportImage: CapturedImage[]) => {
      if (exportFormats.includes(ExportFormat.Pptx) || exportFormats.includes(ExportFormat.Png)) {
        await handlePptxOrPngExport(exportFormats, exportImage);
      }

      if (exportFormats.includes(ExportFormat.Xlsx)) {
        await handleXlsxExport(exportFormats);
      }

      if (exportFormats.length > 0) {
        handleExportedTracking(ElementType.BUTTERFLY_CHART, exportFormats, kpi, sortBy);
      }
    },
    [
      captureOnly,
      onCaptureComplete,
      kpi,
      isDeepDiveView,
      deepDiveBrands,
      period.value,
      timeFrame,
      comparisonPeriod,
      factRevenue,
      appliedCountries,
      appliedProductGroups,
      visibleData,
      pagesConfig,
      sortBy,
      featureOption,
      exportButterflyChart,
      handleExportedTracking,
    ],
  );

  const handleSingleExport = useCallback(
    (img: CapturedImage) => {
      if (singleExportGuard.current) return;
      singleExportGuard.current = true;
      const pageTitle = isDeepDiveView ? viewingBrand : PageName.TRADING_ASSISTANT;

      const bundle = [
        {
          ...img,
          title: pageTitle,
        },
      ];

      if (lastFormats.includes(ExportFormat.Png)) {
        onChartDownload([ExportFormat.Png], bundle);
      }
      if (!isDeepDiveView && lastFormats.includes(ExportFormat.Pptx)) {
        onChartDownload([ExportFormat.Pptx], bundle);
      }
      if (lastFormats.includes(ExportFormat.Xlsx)) {
        onChartDownload([ExportFormat.Xlsx], [{ ...img, title: PageName.TRADING_ASSISTANT }]);
      }
    },
    [lastFormats, isDeepDiveView, viewingBrand, onChartDownload],
  );

  useEffect(() => {
    if (!exportingAll) return;
    if (Object.keys(capturedMap).length !== deepDiveBrands.length) return;

    const images = Object.values(capturedMap);
    onChartDownload([ExportFormat.Pptx], images);
    setExportingAll(false);
    setCapturedMap({});
  }, [capturedMap, exportingAll, deepDiveBrands, onChartDownload]);

  const getChartKey = (branch: string) => `${branch}-${kpi}-${sortBy}-${exportId}`;

  const requestExport = useCallback(
    (formats: ExportFormat[]) => {
      setLastFormats(formats);
      singleExportGuard.current = false;
      setExportId((id) => id + 1);

      if (formats.includes(ExportFormat.Pptx) && isDeepDiveView && deepDiveBrands.length) {
        setCapturedMap({});
        setExportingAll(true);
      }
    },
    [isDeepDiveView, deepDiveBrands.length],
  );

  useEffect(() => {
    if (captureOnly) {
      requestExport([ExportFormat.Pptx]);
    }
  }, [captureOnly, requestExport]);

  const handleDeepDiveExport = (brand: string) => (img: CapturedImage) => {
    setCapturedMap((prev) => (prev[brand] ? prev : { ...prev, [brand]: { ...img, title: brand } }));
  };

  return (
    <div className="mt-4">
      <Card>
        {createInsightDataFn && (
          <Card.Title title="">
            <div
              key={`${isLoading}-${features}`} //NOSONAR typescript:S6551
              className="flex w-full"
            >
              <BrandDependencyFilter
                selectedKpi={kpi}
                setSelectedKpi={setKpi}
                selectedSortBy={sortBy}
                setSelectedSortBy={setSortBy}
                features={features}
                bySegment={bySegment}
                setFeatureOptions={setFeatureOptions}
                isLoading={isLoading}
                onChartDownload={requestExport}
                createInsightDataFn={createInsightDataFn}
                selectedBrands={selectedBrands}
                createViewStateFn={createViewStateFn}
              />
            </div>
          </Card.Title>
        )}
        <Card.Content>
          <BrandDependencyChart
            key={getChartKey(isDeepDiveView ? viewingBrand : 'single')}
            isLoading={isLoading}
            chartData={visibleData}
            kpi={kpi}
            retailerClientName={retailerClientName}
            selectedGrowthType={selectedGrowthType}
            factRevenue={factRevenue}
            sortBy={sortBy}
            bySegment={bySegment}
            isDeepDiveView={isDeepDiveView}
            viewingBrand={viewingBrand}
            brandsColorMap={brandsColorMap.current}
            subSectionForAmplitudeTracking={subSectionForAmplitudeTracking}
            effectiveProductType={effectiveProductType}
            onExport={handleSingleExport}
            isApiError={isApiError}
          />
          {isDeepDiveView && !isLoading && deepDiveBrands.length > 1 && (
            <BrandDeepDiveBrandSelector
              viewingBrand={viewingBrand}
              setViewingBrand={setViewingBrand}
              setIsloading={setIsloading}
              brands={deepDiveBrands}
            />
          )}
          {exportingAll && isDeepDiveView && (
            <div style={{ position: 'absolute', left: -9999, top: 0, visibility: 'hidden' }}>
              {deepDiveBrands.map((brand) => (
                <BrandDependencyChart
                  key={getChartKey(brand)}
                  isLoading={false}
                  chartData={deepDiveDataLookup?.get(brand)?.data ?? []}
                  kpi={kpi}
                  retailerClientName={retailerClientName}
                  selectedGrowthType={selectedGrowthType}
                  factRevenue={factRevenue}
                  sortBy={sortBy}
                  bySegment={bySegment}
                  isDeepDiveView
                  viewingBrand={brand}
                  brandsColorMap={brandsColorMap.current}
                  subSectionForAmplitudeTracking={subSectionForAmplitudeTracking}
                  effectiveProductType={effectiveProductType}
                  onExport={handleDeepDiveExport(brand)}
                  isApiError={isApiError}
                />
              ))}
            </div>
          )}
        </Card.Content>
      </Card>
    </div>
  );
};

export default BrandDependencyCard;
